/**
 * A player's card position: the position most team-mates voted for
 * (player_position_consensus, highest percentage). It's shown on every UT card
 * and the WhatsApp bot quotes it, so both pick it the same way. The top two or
 * three positions are public in words (describePositions); the percentages and
 * vote counts behind them stay private. Pure: bundled by the bot (bot/).
 */

export interface ConsensusRow {
  player_id: string;
  position: string;
  percentage: number | string | null;
}

/**
 * Pick each player's primary position: the highest consensus percentage,
 * ties broken alphabetically so the result is stable.
 */
export function pickPrimaryPositions(rows: ConsensusRow[]): Map<string, string> {
  const best = new Map<string, { position: string; pct: number }>();
  for (const r of rows) {
    const pct = Number(r.percentage ?? 0);
    const cur = best.get(r.player_id);
    if (!cur || pct > cur.pct || (pct === cur.pct && r.position < cur.position)) {
      best.set(r.player_id, { position: r.position, pct });
    }
  }
  const out = new Map<string, string>();
  for (const [id, v] of best) if (v.pct > 0) out.set(id, v.position);
  return out;
}

/**
 * Where team-mates see a player, in words (23 Oct 2026): "Mostly CM or CDM,
 * sometimes CB". Up to three positions. `main` starts with the card position;
 * the rest are close behind it (MAIN_SHARE of its votes), `also` are clearly
 * behind but still real (ALSO_MIN_PCT and ALSO_SHARE). Never percentages or
 * vote counts: many players have a handful of raters, so numbers would show
 * who voted for what.
 */
export interface PositionProfile {
  main: string[];
  also: string[];
}

const MAX_POSITIONS = 3;
const MAIN_SHARE = 0.75;
const ALSO_SHARE = 0.4;
const ALSO_MIN_PCT = 10;

export function pickPositionProfiles(rows: ConsensusRow[]): Map<string, PositionProfile> {
  const byPlayer = new Map<string, { position: string; pct: number }[]>();
  for (const r of rows) {
    const pct = Number(r.percentage ?? 0);
    if (pct <= 0) continue;
    const list = byPlayer.get(r.player_id) ?? [];
    list.push({ position: r.position, pct });
    byPlayer.set(r.player_id, list);
  }
  const out = new Map<string, PositionProfile>();
  for (const [id, list] of byPlayer) {
    // Same order as pickPrimaryPositions, so main[0] is always the card position.
    list.sort((a, b) => b.pct - a.pct || a.position.localeCompare(b.position));
    const top = list[0].pct;
    const main = [list[0].position];
    const also: string[] = [];
    for (const p of list.slice(1)) {
      if (main.length + also.length >= MAX_POSITIONS) break;
      if (!also.length && p.pct >= top * MAIN_SHARE) main.push(p.position);
      else if (p.pct >= ALSO_MIN_PCT && p.pct >= top * ALSO_SHARE) also.push(p.position);
    }
    out.set(id, { main, also });
  }
  return out;
}

const orList = (xs: string[]) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`);

/** "Mostly CM or CDM, sometimes CB" / "Mostly ST". */
export function describePositions(p: PositionProfile): string {
  return `Mostly ${orList(p.main)}${p.also.length ? `, sometimes ${orList(p.also)}` : ''}`;
}
